import React from 'react';
import { Box, Container, Header, Spinner, SpaceBetween } from '@amzn/awsui-components-core';
import { ConnectionsScorecard, CONNECTIONS_THEMES, THEME_DISPLAY_NAMES, RangeIndicator, Theme } from '../types';
import './scorecard-hero.scss';

interface ScorecardHeroProps {
  loading: boolean;
  error: Error | null;
  scorecard: ConnectionsScorecard | null;
  leaderName: string;
  periodLabel: string;
  infoLink?: React.ReactNode;
}

const RANGE_LABEL: Record<NonNullable<RangeIndicator>, string> = {
  above: 'Above',
  within: 'Within',
  below: 'Below'
};

const RANGE_STATUS_CLASS: Record<NonNullable<RangeIndicator>, string> = {
  above: 'sh-status--above',
  within: 'sh-status--within',
  below: 'sh-status--below'
};

const RANGE_ARROW: Record<NonNullable<RangeIndicator>, string> = {
  above: '\u25B2',
  within: '\u25CF',
  below: '\u25BC'
};

const formatPct = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return '-';
  return `${(n * 100).toFixed(1)}%`;
};

// Per-theme gradient accent strip (left edge of each hero card).
const THEME_ACCENT: Record<Theme, string> = {
  CAREER_GROWTH: 'linear-gradient(135deg, #60a5fa, #2f7fe0)',
  CULTURE: 'linear-gradient(135deg, #6ee7b7, #0ea66f)',
  INCLUSION: 'linear-gradient(135deg, #c084fc, #a855f7)',
  JOB_SATISFACTION: 'linear-gradient(135deg, #fbbf24, #f0883e)'
};

/**
 * 4-card hero for the selected leader. One card per Connections theme.
 * Each card: theme name, big score, range chip, sample-size footnote.
 * This is the first thing the user sees — sets the "what's the headline" tone.
 */
export const ScorecardHero: React.FC<ScorecardHeroProps> = ({ loading, error, scorecard, leaderName, periodLabel, infoLink }) => {
  return (
    <Container
      header={
        <Header variant="h2" description={`${leaderName} · ${periodLabel}`} info={infoLink}>
          📋 Org Health Scorecard
        </Header>
      }
    >
      {loading && (
        <Box textAlign="center" padding="xl">
          <Spinner size="big" /> Loading scorecard...
        </Box>
      )}
      {error && !loading && (
        <Box color="text-status-error" padding="m">
          Failed to load scorecard: {error.message}
        </Box>
      )}
      {!loading && !error && !scorecard && (
        <Box color="text-body-secondary" padding="m">
          No scorecard data for the selected leader and period.
        </Box>
      )}
      {!loading && scorecard && (
        <div className="sh-grid">
          {CONNECTIONS_THEMES.map((theme) => {
            const t = scorecard.themes.find((x) => x.theme.toUpperCase() === theme);
            const value = t?.metricValue;
            const range = t?.rangeIndicator as RangeIndicator | null;
            return (
              <div
                key={theme}
                className="cp-hero-card"
                style={
                  {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    minHeight: '120px',
                    '--cp-accent': THEME_ACCENT[theme]
                  } as React.CSSProperties
                }
              >
                <div className="sh-sub">{THEME_DISPLAY_NAMES[theme as Theme]}</div>
                <div className="sh-score">{formatPct(value)}</div>
                {range && (
                  <div className={`sh-range ${RANGE_STATUS_CLASS[range]}`}>
                    <span className="sh-range-arrow">{RANGE_ARROW[range]}</span>
                    <span className="sh-range-label">{RANGE_LABEL[range]} expected range</span>
                  </div>
                )}
                {t && (
                  <div className="sh-muted">
                    n={t.sampleSize ?? '-'} · expected {formatPct(t.benchmarkMin)}-{formatPct(t.benchmarkMax)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Container>
  );
};
